import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { toast } from 'react-toastify';
import GastoDoWhatsApp from '../../components/GastoDoWhatsApp';
import TemplatesDoWhatsApp from '../../components/TemplatesDoWhatsApp';
import http from '../../services/http';
import {
  MENSAGEM_SEM_PERMISSAO,
  corDoEstado,
  departamentosDoNumero,
  estadoDoNumero,
  mensagemDoErro,
  statusDoErro,
  textoDaCredencial,
  tomLegivel,
  urlDoWebhook,
  urlGlobalDoWebhook,
} from '../../utils/numeroDeWhatsapp';

/**
 * A tela de cadastro dos números de WhatsApp do tenant.
 *
 * Existe porque o resto da funcionalidade já estava pronto e cadastrar um
 * número era `INSERT` na mão no banco de produção. O caso que a pediu: SysFar e
 * MAX são duas marcas do MESMO cliente, no mesmo tenant, cada uma com o seu
 * número e o seu menu de departamentos -- "mensagem separada pro Max, mensagem
 * separada para o Sisfar".
 *
 * Duas coisas que a tela NÃO faz, de propósito:
 *
 *   - não mostra o token nem o `app_secret`, nem mascarados com os últimos
 *     dígitos. A API não devolve o valor (nem parcial), então não há o que
 *     mascarar; o que ela mostra é se a credencial EXISTE.
 *   - não deixa o cliente escolher o endereço do webhook. Ele escolhe SE usa
 *     app próprio; o slug é sorteado no servidor, porque um slug escolhido
 *     seria `sysfar`, `max`, `teste` -- adivinhável, e ele é metade do par que
 *     autentica a chamada da Meta.
 *
 * As regras de payload e de mensagem de erro moram em
 * `src/utils/numeroDeWhatsapp.js`: são elas que decidem coisas invisíveis na
 * tela (token em branco MANTÉM o guardado, `departamentos` omitido MANTÉM o
 * menu) e é lá que estão testadas sem renderizar nada.
 */


/**
 * A frase do cabeçalho muda com a aba.
 *
 * O título virou "WhatsApp" porque a tela deixou de ser só a lista de
 * números. Sem trocar a descrição junto, o cabeçalho ficaria explicando
 * números enquanto a pessoa olha um gráfico de gasto.
 */
const DESCRICAO_DA_ABA = {
  numeros: 'Cada número tem a sua saudação e o seu menu de departamentos. É assim que duas marcas da mesma empresa atendem com mensagens diferentes.',
  templates: 'Mensagens aprovadas pela Meta, agrupadas por conta. Uma conta pode ter vários números, e o template vale para todos eles.',
  gastos: 'O que a Meta cobrou por número no mês. Ela informa se cobra, não quanto — o rodapé explica.',
};

const WhatsAppPage = () => {
  const history = useHistory();

  // A aba aberta. Começa em "numeros" porque é a razão de alguém abrir esta
  // tela: cadastrar ou consertar um número. Gasto e templates são consulta.
  const [aba, setAba] = useState('numeros');

  const [numeros, setNumeros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroDaLista, setErroDaLista] = useState('');
  // Separado da MENSAGEM de propósito: o alerta tem um "fechar", e enquanto a
  // condição do estado vazio olhava `erroDaLista`, fechar o alerta de um 500 ou
  // de um timeout trazia de volta "Nenhum número cadastrado" e o botão
  // "Cadastrar primeiro número" -- o mesmo desfecho que essa guarda existe para
  // evitar, a um clique de distância. Este aqui só volta a `false` em
  // `carregar()`, que é quando a lista realmente foi buscada de novo.
  const [falhouAoCarregar, setFalhouAoCarregar] = useState(false);
  // Separado do erro comum de propósito: 403 aqui NÃO é tela quebrada nem lista
  // vazia. O item do menu é liberado por um caminho de permissão mais antigo
  // que o da API, então este caso acontece com o menu à vista -- e "nenhum
  // número cadastrado" faria o cliente cadastrar de novo o que já existe.
  const [semPermissao, setSemPermissao] = useState(false);

  // O 409 do delete vira um aviso COM a saída: guardamos o número para oferecer
  // "desativar" ali mesmo, que é o que resolve sem quebrar as conversas.
  const [conflito, setConflito] = useState(null);
  const [desativando, setDesativando] = useState(false);

  const baseDaApi = process.env.REACT_APP_API_URL || '';
  const urlGlobal = urlGlobalDoWebhook(baseDaApi);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroDaLista('');
    setFalhouAoCarregar(false);
    setSemPermissao(false);
    try {
      // O `GET /groups` SAIU daqui junto com o formulário. A lista nunca
      // precisou dele: o menu que cada cartão mostra vem do próprio número
      // (`departamentosDoNumero` lê o pivô, que já traz a descrição). Ele
      // existia só para alimentar o seletor do diálogo.
      //
      // Uma requisição a menos por abertura da tela, e o estado de erro dele
      // foi junto -- aqui não havia mais nada para avisar.
      const resposta = await http.get('/whatsapp-numbers');

      const lista = Array.isArray(resposta?.data?.data) ? resposta.data.data : [];
      setNumeros(lista);
    } catch (e) {
      if (statusDoErro(e) === 403) {
        setSemPermissao(true);
      } else {
        setErroDaLista(mensagemDoErro(e, 'Não foi possível carregar os números de WhatsApp.'));
        setFalhouAoCarregar(true);
      }
      setNumeros([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const copiar = async (valor) => {
    try {
      await navigator.clipboard.writeText(valor);
      toast.success('Copiado.');
    } catch (e) {
      // Navegador sem permissão de área de transferência: o valor continua
      // visível e selecionável na tela, que é o que importa.
      toast.warning('Não foi possível copiar. Selecione o texto e copie à mão.');
    }
  };
  const excluir = async (numero) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o número "${numero.rotulo}"? Isto não pode ser desfeito.`)) return;

    setConflito(null);
    setErroDaLista('');
    try {
      await http.delete(`/whatsapp-numbers/${numero.id}`);
      await carregar();
      toast.success('Número excluído.');
    } catch (e) {
      const status = statusDoErro(e);
      const mensagem = mensagemDoErro(e, 'Não foi possível excluir o número.');
      if (status === 409) {
        setConflito({ numero, mensagem });
      } else {
        setErroDaLista(mensagem);
      }
    }
  };

  const desativar = async (numero) => {
    // Trava de clique: dois cliques no botão do alerta do 409 disparavam dois
    // `PATCH`. É idempotente, então o efeito era só ruído -- mas ruído que
    // aparece no log de quem for investigar o próximo problema de verdade.
    //
    // Quem trava é o `disabled={desativando}` no botão, e não um `if` aqui. A
    // rodada de mutação provou a diferença: apagar o `if` não quebrava teste
    // nenhum (o botão já estava desabilitado antes do segundo clique), o que
    // torna o `if` código morto. Apagar o `disabled` quebra -- então é ele a
    // proteção de verdade, e é ele que o teste prende.
    setDesativando(true);
    try {
      // `ativo` como booleano de verdade: em branco a API não desativa (e nem
      // ativa), e aí o botão não valeria nada.
      await http.patch(`/whatsapp-numbers/${numero.id}`, { ativo: false });
      setConflito(null);
      await carregar();
      // "Parou de receber", e não "saiu do roteamento": o segundo dava a
      // entender que o número ficou inerte, e ele continua respondendo as
      // conversas que já entraram por ele.
      toast.success('Número desativado. Ele parou de receber mensagem nova e o histórico continua.');
    } catch (e) {
      setErroDaLista(mensagemDoErro(e, 'Não foi possível desativar o número.'));
    } finally {
      setDesativando(false);
    }
  };

  const campoCopiavel = (rotulo, valor, ajuda) => (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {rotulo}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.primary' }}
        >
          {valor}
        </Typography>
        <Tooltip title="Copiar">
          <IconButton size="small" onClick={() => copiar(valor)} aria-label={`Copiar ${rotulo}`}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {ajuda && (
        <Typography variant="caption" color="text.secondary">{ajuda}</Typography>
      )}
    </Box>
  );

  // O cadastro e a edicao sao PAGINA desde que o formulario (390 linhas de
  // campo) saiu do dialogo `maxWidth="sm"`. Ver `pages/WhatsAppNumeroPage`.
  const abrirCadastro = () => history.push("/whatsapp/novo");
  const abrirEdicao = (numero) => history.push(`/whatsapp/${numero.id}`);


  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>WhatsApp</Typography>
            <Typography variant="body2" color="text.secondary">
              {DESCRICAO_DA_ABA[aba]}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Recarregar">
            <IconButton onClick={carregar} aria-label="Recarregar">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {/* Só na aba de números: "Novo número" no meio de templates ou de
              gastos é um botão que não pertence ao que está na tela. */}
          {aba === 'numeros' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCadastro} disabled={semPermissao}>
              Novo número
            </Button>
          )}
        </Box>
      </Box>

      {semPermissao ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          {/* `text.secondary`, e nao `text.disabled`: medido, o `disabled` sobre o
              papel branco do tema claro da 2,56:1 -- abaixo dos 3:1 de grafico
              grande. O `secondary` da 7,58:1 no claro e 6,28:1 no escuro. */}
          <LockOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Acesso não liberado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {MENSAGEM_SEM_PERMISSAO}
          </Typography>
        </Paper>
      ) : (
        <>
          {/*
            As três áreas viviam empilhadas numa rolagem só: cadastro, gasto e
            templates, um debaixo do outro. Quem abria a tela para consertar um
            número passava por um gráfico de gasto e por uma lista que fala com
            a Meta no caminho.

            Templates ficam em aba PRÓPRIA, e não dentro de cada número, porque
            o template pertence à CONTA (WABA) -- vários números podem
            compartilhá-la. Dentro do número, o mesmo template apareceria
            duplicado em cada um, e excluir a partir de um apagaria para os
            outros (a Meta exclui pelo NOME, levando todos os idiomas junto),
            sem nada na tela dizendo isso.
          */}
          <Tabs
            value={aba}
            onChange={(e, nova) => setAba(nova)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="numeros" label="Números" />
            <Tab value="templates" label="Templates" />
            <Tab value="gastos" label="Gastos" />
          </Tabs>

          {aba === 'gastos' && <GastoDoWhatsApp />}

          {aba === 'templates' && <TemplatesDoWhatsApp />}

          {aba === 'numeros' && (
          <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Configuração do webhook na Meta
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Este é o endereço dos números que usam o app da plataforma. Quem usa app próprio tem
              um endereço só dele, mostrado no cartão do número.
            </Typography>
            {urlGlobal
              ? campoCopiavel('URL de callback', urlGlobal)
              : (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  O endereço da API não está configurado neste painel, então não há URL de webhook
                  para mostrar. Peça ao suporte.
                </Alert>
              )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              O token de verificação (<em>verify token</em>) é o mesmo para toda a plataforma e fica
              guardado no servidor — ele não é exibido aqui nem pela API. Peça-o ao suporte na hora
              de configurar o webhook na Meta.
            </Typography>
          </Paper>

          {/* Gasto e templates saíram daqui para abas próprias. Os dois
              continuam em componentes separados pelo mesmo motivo de sempre:
              cada um carrega os próprios dados e tem os próprios erros, e o
              de templates fala com a META a cada abertura -- uma falha dela
              não pode derrubar a tela de cadastro, que é justamente a que
              alguém abre para consertar um número que não está funcionando.

              Agora eles nem são montados enquanto a aba não é aberta, o que
              torna essa separação mais forte, não mais fraca. */}

          {erroDaLista && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroDaLista('')}>
              {erroDaLista}
            </Alert>
          )}

          {conflito && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              onClose={() => setConflito(null)}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={desativando}
                  onClick={() => desativar(conflito.numero)}
                >
                  Desativar
                </Button>
              }
            >
              {conflito.mensagem}
            </Alert>
          )}

          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (numeros.length === 0 && !falhouAoCarregar) ? (
            /* `&& !falhouAoCarregar` NAO e detalhe de layout. Timeout do axios
               (10 s) ou 500 caiam aqui com a lista zerada, e a tela mostrava o erro
               E "Nenhum numero cadastrado" com o botao "Cadastrar primeiro numero"
               -- exatamente a confusao que o tratamento do 403 existe para evitar,
               com o mesmo desfecho: o cliente recadastra um numero que ja existe e
               colide no `phone_number_id`, que e unico na plataforma inteira.

               Pelo ESTADO da carga, e nao pela mensagem: o alerta tem um botao de
               fechar, e olhar `erroDaLista` punha a mesma confusao a um clique de
               distancia. */
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <WhatsAppIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>Nenhum número cadastrado</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cadastre o número com os dados do app na Meta e monte o menu de departamentos.
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={abrirCadastro}>
                Cadastrar primeiro número
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {numeros.map((numero) => {
                const estado = estadoDoNumero(numero);
                const url = urlDoWebhook(numero, baseDaApi);
                // Pela `ordem` do pivô, como o formulário. Numerar pela ordem
                // crua do array coincide só enquanto o `orderBy` da relação
                // existir no servidor -- e no dia em que ele sair, o cartão
                // mentiria sobre "menu que o cliente vê" com o formulário certo.
                const menu = departamentosDoNumero(numero);
                const familiaDoEstado = corDoEstado(estado);

                return (
                  <Paper
                    key={numero.id}
                    variant="outlined"
                    // Número inativo fica visivelmente apagado -- mas pela
                    // opacidade, não por uma cor fixa, que mudaria de sentido no
                    // tema escuro.
                    sx={{ p: 2, opacity: numero.ativo ? 1 : 0.75 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {numero.rotulo}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {numero.display_number}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                          {/* Cor por `sx`, e nao por `color=`: o tom `main` de
                              warning e success nao alcanca 4,5:1 sobre o papel
                              branco do tema claro. Ver `tomLegivel`. */}
                          {/* `data-testid` na raiz porque a cor mora na raiz: e o
                              teste de CONTRASTE precisa medir o elemento que a
                              carrega, nao o span do rotulo. */}
                          <Chip
                            size="small"
                            label={estado}
                            variant="outlined"
                            data-testid={`chip-estado-${numero.id}`}
                            sx={{
                              textTransform: 'capitalize',
                              color: tomLegivel(familiaDoEstado),
                              borderColor: tomLegivel(familiaDoEstado),
                            }}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={textoDaCredencial(numero)}
                            data-testid={`chip-credencial-${numero.id}`}
                            sx={{
                              color: tomLegivel(numero.tem_token ? 'default' : 'error'),
                              borderColor: tomLegivel(numero.tem_token ? 'default' : 'error'),
                            }}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={numero.usa_app_proprio ? 'app próprio' : 'app da plataforma'}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEdicao(numero)} aria-label={`Editar ${numero.rotulo}`}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => excluir(numero)} aria-label={`Excluir ${numero.rotulo}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Menu que o cliente vê no WhatsApp
                    </Typography>
                    {menu.length === 0 ? (
                      <Typography variant="body2" sx={{ color: tomLegivel('warning') }}>
                        Nenhum departamento. A conversa cai na saudação sem opção para o cliente
                        escolher — monte o menu antes de divulgar o número.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {menu.map((departamento, indice) => (
                          <Chip
                            key={departamento.id}
                            size="small"
                            label={`${indice + 1}. ${departamento.description || departamento.code}`}
                          />
                        ))}
                      </Box>
                    )}

                    {numero.usa_app_proprio && (
                      url
                        ? campoCopiavel(
                          'URL de callback deste número (app próprio)',
                          url,
                          'Cole este endereço na configuração do webhook do app do cliente na Meta.',
                        )
                        : (
                          <Alert severity="warning" sx={{ mt: 1.5 }}>
                            Este número está marcado como app próprio, mas não tem endereço de
                            webhook. Salve-o novamente informando o segredo do app.
                          </Alert>
                        )
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
          </>
          )}
        </>
      )}

    </Box>
  );
};

export default WhatsAppPage;
